import React, { useState, useEffect } from "react";
import { Phone, Menu, X } from "lucide-react";
import { Button } from "./ui/button";
import { useClinic } from "../context/ClinicContext";

const Header = () => {
  const { clinicInfo } = useClinic();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <header
      className={`navigation-header fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled ? "shadow-refined" : ""
      }`}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-16">
        <div className="flex items-center justify-between h-16 sm:h-20 lg:h-24">
          {/* Logo */}
          <button
            onClick={() => scrollToSection("home")}
            className="navigation-logo hover:opacity-70 transition-opacity duration-300"
          >
            {clinicInfo.name}
          </button>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            <ul className="navigation-menu flex items-center gap-8">
              <li>
                <button
                  onClick={() => scrollToSection("home")}
                  className="navigation-link"
                >
                  Home
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection("services")}
                  className="navigation-link"
                >
                  Services
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection("about")}
                  className="navigation-link"
                >
                  About
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection("faq")}
                  className="navigation-link"
                >
                  FAQ
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection("testimonials")}
                  className="navigation-link"
                >
                  Testimonials
                </button>
              </li>
            </ul>
          </nav>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-3 lg:gap-6 flex-shrink-0">
            <a
              href={`tel:${clinicInfo.phone}`}
              className="hidden sm:flex items-center gap-1 sm:gap-2 text-[#57534E] hover:text-[#0F766E] transition-colors duration-300 whitespace-nowrap"
              title={clinicInfo.phone}
            >
              <Phone size={18} strokeWidth={1.5} className="flex-shrink-0" />
              <span className="text-xs sm:text-sm font-medium tracking-wide hidden xl:inline">
                {clinicInfo.phone}
              </span>
            </a>
            <Button
              onClick={() => scrollToSection("appointments")}
              className="btn-primary hidden lg:flex text-xs lg:text-sm px-3 lg:px-8"
            >
              Book Appointment
            </Button>
            <a
              href={`tel:${clinicInfo.phone}`}
              className="flex md:hidden items-center justify-center text-[#0F766E] hover:text-[#0F766E] transition-colors duration-300 p-2"
              title="Call us"
            >
              <Phone size={20} strokeWidth={1.5} />
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden text-[#1C1917] hover:text-[#0F766E] transition-colors duration-300 p-2"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? (
              <X size={26} strokeWidth={1.5} />
            ) : (
              <Menu size={26} strokeWidth={1.5} />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden absolute top-16 sm:top-20 left-0 right-0 bg-[#FAFAF9] backdrop-filter backdrop-blur-xl border-t border-[#E7E5E4] shadow-elevated">
            <nav className="container mx-auto px-4 sm:px-6 py-4 sm:py-8">
              <ul className="flex flex-col gap-2">
                <li>
                  <button
                    onClick={() => scrollToSection("home")}
                    className="block w-full text-left py-3 text-[#57534E] hover:text-[#1C1917] transition-colors duration-300 text-base font-light"
                  >
                    Home
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => scrollToSection("services")}
                    className="block w-full text-left py-3 text-[#57534E] hover:text-[#1C1917] transition-colors duration-300 text-base font-light"
                  >
                    Services
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => scrollToSection("about")}
                    className="block w-full text-left py-3 text-[#57534E] hover:text-[#1C1917] transition-colors duration-300 text-base font-light"
                  >
                    About
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => scrollToSection("faq")}
                    className="block w-full text-left py-3 text-[#57534E] hover:text-[#1C1917] transition-colors duration-300 text-base font-light"
                  >
                    FAQ
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => scrollToSection("testimonials")}
                    className="block w-full text-left py-3 text-[#57534E] hover:text-[#1C1917] transition-colors duration-300 text-base font-light"
                  >
                    Testimonials
                  </button>
                </li>
                <li className="pt-4 border-t border-[#E7E5E4]">
                  <a
                    href={`tel:${clinicInfo.phone}`}
                    className="flex items-center gap-2 text-[#57534E] hover:text-[#0F766E] transition-colors duration-300 py-3"
                  >
                    <Phone size={18} strokeWidth={1.5} />
                    <span className="text-base font-light">
                      {clinicInfo.phone}
                    </span>
                  </a>
                </li>
                <li>
                  <Button
                    onClick={() => scrollToSection("appointments")}
                    className="btn-primary w-full"
                  >
                    Book Appointment
                  </Button>
                </li>
              </ul>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
